// ============================================
// verify-email-sent.tsx — R24 "check your inbox" page
// ============================================
//
// Dedicated landing page shown right after a successful signup. The
// previous implementation rendered this state inline on /register, which
// meant a refresh lost the message and the URL stayed on the form.
//
// It is a normal public route, so it inherits the app-wide providers:
//   - I18nProvider  → fully translated (EN/FR)
//   - ThemeProvider → the interactive wallpaper background
//     (InteractiveBackground is mounted globally in theme-provider)
//
// Query params:
//   email    the address the link was sent to (displayed, and used by
//            the resend action)
//   redirect optional post-login destination, forwarded to /login so a
//            deep link is not lost while the user confirms the address

import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Loader2, MailCheck } from 'lucide-react';
import { useResendVerification } from '@/hooks/use-auth';
import { useI18n } from '@/lib/i18n';
import { Button } from '@/components/ui/button';
import {
    Card,
    CardContent,
    CardDescription,
    CardFooter,
    CardHeader,
    CardTitle,
} from '@/components/ui/card';
import { withRedirect } from '@/lib/auth-redirect';

export function VerifyEmailSentPage() {
    const { t } = useI18n();
    const [searchParams] = useSearchParams();
    const email = searchParams.get('email') || '';
    const redirectTo = searchParams.get('redirect') || '';
    const resendVerification = useResendVerification();
    const [resent, setResent] = useState(false);

    return (
        <div className="flex min-h-screen items-center justify-center p-4">
            <Card className="w-full max-w-md">
                <CardHeader className="text-center">
                    <MailCheck className="mx-auto mb-2 h-12 w-12 text-primary" />
                    <CardTitle>{t('auth.checkYourEmailTitle')}</CardTitle>
                    <CardDescription>{t('auth.checkYourEmailBody')}</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4 text-center">
                    {email && <p className="text-sm font-medium text-foreground">{email}</p>}
                    <p className="text-sm text-foreground-muted">{t('auth.checkYourEmailHint')}</p>

                    {resent ? (
                        <p
                            className="flex items-center justify-center gap-2 rounded-lg border border-status-success/30 bg-status-success/10 p-3 text-sm text-status-success"
                            data-testid="verify-email-sent-confirmation"
                        >
                            <MailCheck className="h-4 w-4" />
                            {t('auth.verificationResentBody')}
                        </p>
                    ) : (
                        <Button
                            type="button"
                            variant="outline"
                            className="w-full"
                            disabled={resendVerification.isPending || !email}
                            onClick={() => {
                                resendVerification.mutate(email, {
                                    onSuccess: () => setResent(true),
                                });
                            }}
                            data-testid="verify-email-sent-resend"
                        >
                            {resendVerification.isPending ? (
                                <>
                                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                    {t('auth.sending')}
                                </>
                            ) : (
                                t('auth.resendVerification')
                            )}
                        </Button>
                    )}
                </CardContent>
                <CardFooter className="justify-center">
                    <p className="text-center text-sm text-foreground-muted">
                        <Link
                            to={withRedirect('/login', redirectTo)}
                            className="text-primary hover:underline font-medium"
                        >
                            {t('auth.backToSignIn')}
                        </Link>
                    </p>
                </CardFooter>
            </Card>
        </div>
    );
}
